import { CustomAnimationSequence, CustomAnimationFrame } from '../assetConfig';
import { globalAssetsRef } from '../assetStore';

export class CustomSpriteAnimator {
  private animationSetName: string;
  // private allAnimations: Record<string, CustomAnimationSequence>; // Removed, accessing globalAssetsRef directly
  public currentAnimationName: string | null = null;
  // private currentSequence: CustomAnimationSequence | null = null; // Removed caching
  
  private get currentSequence(): CustomAnimationSequence | null {
    if (!this.currentAnimationName) return null;
    return globalAssetsRef.current?.customAnimations?.[this.animationSetName]?.[this.currentAnimationName] || null;
  }

  private currentFrameIndex: number = 0;
  private frameTimer: number = 0;
  private isPlaying: boolean = false;
  private onAnimationEndCallback: (() => void) | null = null;
  private isInLoopPhase: boolean = false; // Track if we're in the loop phase for number[] loops
  private timeScale: number = 1;
  // Lightweight event system for per-frame triggers
  private eventListeners: Map<string, Set<() => void>> = new Map();

  public x = -1000;
  public y = -1000;
  public rotation = 0;
  public scale = 1;
  public scaleX = 1;
  public scaleY = 1;
  public hidden: boolean = false;
  public blendMode: GlobalCompositeOperation = 'source-over';

  constructor(animationSetName: string) {
    this.animationSetName = animationSetName;
  }

  public render(context: CanvasRenderingContext2D, getImage: (key: string) => HTMLImageElement, fadeOpacity?: number) {
    if (this.hidden) return;

    const frameDetails = this.getCurrentFrameDetails();
    if (frameDetails) {
      const renderSize = frameDetails.renderSize ?? { width: frameDetails.width, height: frameDetails.height };
      const renderAnchor = frameDetails.renderAnchor ?? frameDetails.anchor;
      const image = getImage(frameDetails.imageKey);
      if (!image) {
        return;
      }

      context.save();
      context.globalCompositeOperation = this.blendMode;
      context.globalAlpha *= fadeOpacity ?? 1;
      context.translate(this.x, this.y);
      context.rotate(this.rotation);
      context.scale(this.scale * this.scaleX, this.scale * this.scaleY);
      context.translate(-renderSize.width * renderAnchor.x, -renderSize.height * renderAnchor.y);
      context.drawImage(image, 0, 0, renderSize.width, renderSize.height);
      context.restore();
    }
  }

  public play(animationName: string, forceRestart: boolean = false): void {
    // Check existence dynamically
    const allAnimations = globalAssetsRef.current?.customAnimations?.[this.animationSetName];
    if (!allAnimations || !allAnimations[animationName]) {
      // console.warn(`Animation "${animationName}" not found for set "${this.animationSetName}".`);
      this.stop();
      return;
    }

    if (this.currentAnimationName === animationName && !forceRestart && this.isPlaying) {
      return; // Already playing this animation and not forcing restart
    }


    this.currentAnimationName = animationName;
    // this.currentSequence = allAnimations[animationName]; // Removed
    this.currentFrameIndex = 0;
    this.frameTimer = 0;
    this.isPlaying = true;
    this.isInLoopPhase = false; // Reset loop phase
  }

  public update(dt: number): void {
    const sequence = this.currentSequence;
    if (!this.isPlaying || !sequence || sequence.frames.length === 0) {
      return;
    }

    const effectiveTimeScale = Number.isFinite(this.timeScale) && this.timeScale >= 0 ? this.timeScale : 1;
    if (effectiveTimeScale === 0) {
      return;
    }

    this.frameTimer += dt * effectiveTimeScale;
    
    // Frame duration is now solely determined by the sequence's frameRate
    const frameDuration = 1 / sequence.frameRate;

    // Loop to advance frames if enough time has passed for one or more frames
    while (this.frameTimer >= frameDuration && this.isPlaying) { // Check isPlaying in loop condition for immediate stop
      // Fire per-frame events for the frame we've just entered
      // 1) sequence-level mapping
      if (sequence.frameEvents && sequence.frameEvents[this.currentFrameIndex]) {
        const events = sequence.frameEvents[this.currentFrameIndex];
        events.forEach((eventName) => this.emit(eventName));
      }
      // 2) frame-level events embedded in frames[] definitions
      const frameDataForEvents = sequence.frames[this.currentFrameIndex];
      if (frameDataForEvents && Array.isArray(frameDataForEvents.events)) {
        frameDataForEvents.events.forEach((eventName) => this.emit(eventName));
      }

      this.frameTimer -= frameDuration; 
      this.currentFrameIndex++;

      // Handle different loop types
      if (this.currentFrameIndex >= sequence.frames.length) {
        if (sequence.loop === true) {
          // Boolean loop: restart from beginning
          this.currentFrameIndex = 0;
        } else if (Array.isArray(sequence.loop) && sequence.loop.length > 0) {
          // Number array loop: start looping through specific frame indices
          this.isInLoopPhase = true;
          this.currentFrameIndex = sequence.loop[0];
        } else {
          // No loop: stay on last frame and stop
          this.currentFrameIndex = sequence.frames.length - 1;
          this.isPlaying = false;
          if (this.onAnimationEndCallback) {
            this.onAnimationEndCallback();
          }
        }
      } else if (this.isInLoopPhase && Array.isArray(sequence.loop)) {
        // We're in the loop phase, cycle through the loop indices
        const currentIndexInLoop = sequence.loop.indexOf(this.currentFrameIndex - 1);
        if (currentIndexInLoop !== -1) {
          const nextIndexInLoop = (currentIndexInLoop + 1) % sequence.loop.length;
          this.currentFrameIndex = sequence.loop[nextIndexInLoop];
        } else {
          // Current frame is not in loop array, go to first loop frame
          this.currentFrameIndex = sequence.loop[0];
        }
      }
    }
  }

  public getCurrentFrameDetails(): {
    imageKey: string;
    width: number;
    height: number;
    anchor: { x: number; y: number };
    renderAnchor?: { x: number; y: number };
    renderSize?: { width: number; height: number };
  } | null {
    const sequence = this.currentSequence;
    if (!sequence || sequence.frames.length === 0) {
      return null;
    }
    const frameData = sequence.frames[this.currentFrameIndex];
    // Ensure frameData is valid before trying to access imageKey, though type system should help
    if (!frameData) {
        console.warn(`Current frame data is undefined for animation ${this.currentAnimationName} at index ${this.currentFrameIndex}`);
        return null;
    }
    const logicalWidth = sequence.frameWidth;
    const logicalHeight = sequence.frameHeight;
    const logicalAnchor = sequence.anchor;

    if (!frameData.size) {
      return {
        imageKey: frameData.imageKey,
        width: logicalWidth,
        height: logicalHeight,
        anchor: logicalAnchor,
      };
    }

    const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

    const { originalSize, croppedSize, bbox } = frameData.size;
    const [originalWidth, originalHeight] = originalSize;
    const [croppedWidth, croppedHeight] = croppedSize;
    const [bboxLeft, bboxTop] = bbox;

    const safeOriginalWidth = originalWidth || logicalWidth;
    const safeOriginalHeight = originalHeight || logicalHeight;
    const safeCroppedWidth = croppedWidth || safeOriginalWidth;
    const safeCroppedHeight = croppedHeight || safeOriginalHeight;

    const anchorPixelX = safeOriginalWidth * logicalAnchor.x;
    const anchorPixelY = safeOriginalHeight * logicalAnchor.y;

    const renderAnchor = {
      x: clamp((anchorPixelX - bboxLeft) / safeCroppedWidth, 0, 1),
      y: clamp((anchorPixelY - bboxTop) / safeCroppedHeight, 0, 1),
    };

    const renderSize = {
      width: safeCroppedWidth,
      height: safeCroppedHeight,
    };

    return {
      imageKey: frameData.imageKey,
      width: logicalWidth,
      height: logicalHeight,
      anchor: logicalAnchor,
      renderAnchor,
      renderSize,
    };
  }

  public getAnimation(name: string) {
    const allAnimations = globalAssetsRef.current?.customAnimations?.[this.animationSetName];
    if (!allAnimations || !allAnimations[name]) {
      // console.warn(`Animation "${name}" not found for set "${this.animationSetName}".`);
      return null;
    }
    const animation = allAnimations[name];
    return animation
  }

  public stop(): void {
    this.isPlaying = false;
    this.isInLoopPhase = false; // Reset loop phase
    // Resetting frameTimer and currentFrameIndex might be desired on stop
    // this.frameTimer = 0;
    // this.currentFrameIndex = 0; // Or to the last frame if that's preferred for non-looping animations
  }

  public setOnAnimationEnd(callback: () => void): void {
    this.onAnimationEndCallback = callback;
  }

  public getIsPlaying(): boolean {
    return this.isPlaying;
  }

  public getCurrentAnimationName(): string | null {
    return this.currentAnimationName;
  }

  // Event API
  public on(eventName: string, handler: () => void) {
    if (!this.eventListeners.has(eventName)) this.eventListeners.set(eventName, new Set());
    this.eventListeners.get(eventName)!.add(handler);
  }

  public off(eventName: string, handler: () => void) {
    const set = this.eventListeners.get(eventName);
    if (!set) return;
    set.delete(handler);
    if (set.size === 0) this.eventListeners.delete(eventName);
  }

  private emit(eventName: string) {
    const set = this.eventListeners.get(eventName);
    if (!set) return;
    set.forEach(fn => {
      try { fn(); } catch (e) { /* noop */ }
    });
  }

  public setTimeScale(scale: number) {
    if (!Number.isFinite(scale)) {
      return;
    }
    this.timeScale = Math.max(0, scale);
  }

  public getTimeScale() {
    return this.timeScale;
  }

  public setFrameIndex(index: number) {
    if (!this.currentSequence) return;
    const clamped = Math.max(0, Math.min(index, this.currentSequence.frames.length - 1));
    this.currentFrameIndex = clamped;
    this.frameTimer = 0;
  }

  public getCurrentFrameIndex() {
    return this.currentFrameIndex;
  }

  public getFrameCount() {
    return this.currentSequence?.frames.length ?? 0;
  }
}
